import type {
  TransformAttributeReferenceItem,
  TransformFunctionItem,
  TransformFunctionParam,
  TransformFunctionPreviewSupport
} from '../types/transform-script';
import { parseTransformScript } from '../transform-script-parser-peggy';
import { formatTransformScriptReference } from './reference';

export { parseTransformScript } from '../transform-script-parser-peggy';

function createParams(params: Array<{ label: string; type: TransformFunctionParam['type']; value?: string }>) {
  return params.map(param => ({
    ...param,
    value: param.value ?? '',
    useRef: false
  }));
}

const APPROX_PREVIEW_FUNCTIONS = new Set([
  'autonumber',
  'base64_to_s3',
  'encrypt_to_s3',
  's3_to_decrypt',
  's3_to_base64',
  'generate_jwt',
  'convert_redirect_id_to_contact_id'
]);

export function getTransformFunctionPreviewSupport(name: string): TransformFunctionPreviewSupport {
  return APPROX_PREVIEW_FUNCTIONS.has(name) ? 'approx' : 'exact';
}

export const transformScriptFunctionCatalog: Omit<TransformFunctionItem, 'id'>[] = [
  {
    name: 'add_region_phone_number',
    color: '#F2EAC4',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Region', type: 'string' }])
    ,previewSupport: getTransformFunctionPreviewSupport('add_region_phone_number')
  },
  {
    name: 'build_column',
    color: '#d6d445',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([
      { label: 'Column Name', type: 'string' },
      { label: 'Column Value', type: 'string' }
    ]),
    previewSupport: getTransformFunctionPreviewSupport('build_column')
  },
  {
    name: 'autonumber',
    color: '#95DBDA',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Prefix', type: 'string' }]),
    previewSupport: getTransformFunctionPreviewSupport('autonumber')
  },
  {
    name: 'base64_to_s3',
    color: '#d6d445',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([
      { label: 'Path', type: 'string' },
      { label: 'File name prefix', type: 'string' },
      { label: 'Storage mode (public/private)', type: 'string', value: 'private' }
    ]),
    previewSupport: getTransformFunctionPreviewSupport('base64_to_s3')
  },
  {
    name: 'encrypt_to_s3',
    color: '#d6d445',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([
      { label: 'Path', type: 'string' },
      { label: 'File name prefix', type: 'string' }
    ]),
    previewSupport: getTransformFunctionPreviewSupport('encrypt_to_s3')
  },
  {
    name: 's3_to_decrypt',
    color: '#d6d445',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: [],
    previewSupport: getTransformFunctionPreviewSupport('s3_to_decrypt')
  },
  {
    name: 's3_to_base64',
    color: '#d6d445',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Storage mode (public/private)', type: 'string', value: 'private' }]),
    previewSupport: getTransformFunctionPreviewSupport('s3_to_base64')
  },
  {
    name: 'concat',
    color: '#9AE3B7',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([
      { label: 'Concat with', type: 'string' },
      { label: 'Seperate With Space', type: 'boolean', value: 'false' }
    ]),
    previewSupport: getTransformFunctionPreviewSupport('concat')
  },
  {
    name: 'convert_timezone',
    color: '#d9771c',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([
      { label: 'Source Offset', type: 'int', value: '0' },
      { label: 'Destination Offset', type: 'int', value: '0' }
    ]),
    previewSupport: getTransformFunctionPreviewSupport('convert_timezone')
  },
  {
    name: 'datetime',
    color: '#FD7BED',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'DateTime layout', type: 'string', value: 'yyyy-MM-dd HH:mm:ss' }]),
    previewSupport: getTransformFunctionPreviewSupport('datetime')
  },
  {
    name: 'to_system_datetime',
    color: '#FD7BED',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Source DateTime layout', type: 'string', value: 'yyyy-MM-dd HH:mm:ss' }]),
    previewSupport: getTransformFunctionPreviewSupport('to_system_datetime')
  },
  {
    name: 'datetime_to_unix_timestamp',
    color: '#d9771c',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Input Offset', type: 'int', value: '0' }]),
    previewSupport: getTransformFunctionPreviewSupport('datetime_to_unix_timestamp')
  },
  {
    name: 'exit_if_equal',
    color: '#ff758c',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([
      { label: 'Compare With', type: 'string' },
      { label: 'Fallback Value', type: 'string' }
    ]),
    previewSupport: getTransformFunctionPreviewSupport('exit_if_equal')
  },
  {
    name: 'explode_by_pipe',
    color: '#E6CDA3',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Select position', type: 'int', value: '0' }]),
    previewSupport: getTransformFunctionPreviewSupport('explode_by_pipe')
  },
  {
    name: 'format_date_string',
    color: '#FD7BED',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Format Date layout', type: 'string', value: 'yyyy-MM-ddTHH:mm:ss.000000Z' }]),
    previewSupport: getTransformFunctionPreviewSupport('format_date_string')
  },
  {
    name: 'from_system_datetime',
    color: '#FD7BED',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Output DateTime layout', type: 'string', value: 'yyyy-MM-ddTHH:mm:ss.000000Z' }]),
    previewSupport: getTransformFunctionPreviewSupport('from_system_datetime')
  },
  {
    name: 'generate_jwt',
    color: '#9BAADD',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([
      { label: 'Issuer', type: 'string' },
      { label: 'Secret (hex)', type: 'string' }
    ]),
    previewSupport: getTransformFunctionPreviewSupport('generate_jwt')
  },
  {
    name: 'hash',
    color: '#9BAADD',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Algorithm', type: 'string' }]),
    previewSupport: getTransformFunctionPreviewSupport('hash')
  },
  {
    name: 'decode',
    color: '#9BAADD',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Method (base64/base64url/url/hex)', type: 'string', value: 'base64' }]),
    previewSupport: getTransformFunctionPreviewSupport('decode')
  },
  {
    name: 'hex_utf16',
    color: '#9BAADD',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: [],
    previewSupport: getTransformFunctionPreviewSupport('hex_utf16')
  },
  {
    name: 'binary_uuid_to_string',
    color: '#9BAADD',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: [],
    previewSupport: getTransformFunctionPreviewSupport('binary_uuid_to_string')
  },
  {
    name: 'math_expressions',
    color: '#4DC0BF',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Math expressions', type: 'string' }]),
    previewSupport: getTransformFunctionPreviewSupport('math_expressions')
  },
  {
    name: 'overwrite_value',
    color: '#CA9E54',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Value', type: 'string' }]),
    previewSupport: getTransformFunctionPreviewSupport('overwrite_value')
  },
  {
    name: 'prefix_with',
    color: '#F2EAC4',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Prefix', type: 'string' }]),
    previewSupport: getTransformFunctionPreviewSupport('prefix_with')
  },
  {
    name: 'read_from_array',
    color: '#4DC0BF',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Read from array', type: 'string' }]),
    previewSupport: getTransformFunctionPreviewSupport('read_from_array')
  },
  {
    name: 'read_from_json',
    color: '#4DC0BF',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Read from json', type: 'string' }]),
    previewSupport: getTransformFunctionPreviewSupport('read_from_json')
  },
  {
    name: 'repeat_string',
    color: '#F0A8A8',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Repeat times', type: 'int', value: '0' }]),
    previewSupport: getTransformFunctionPreviewSupport('repeat_string')
  },
  {
    name: 'replace_string',
    color: '#349A89',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([
      { label: 'Find', type: 'string' },
      { label: 'Replace With', type: 'string' }
    ]),
    previewSupport: getTransformFunctionPreviewSupport('replace_string')
  },
  {
    name: 'split_string',
    color: '#EAB0E3',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([
      { label: 'Seperator', type: 'string' },
      { label: 'Select position', type: 'int', value: '0' }
    ]),
    previewSupport: getTransformFunctionPreviewSupport('split_string')
  },
  {
    name: 'subfix_with',
    color: '#95DBDA',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Subfix', type: 'string' }]),
    previewSupport: getTransformFunctionPreviewSupport('subfix_with')
  },
  {
    name: 'trim_space',
    color: '#9BAADD',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: [],
    previewSupport: getTransformFunctionPreviewSupport('trim_space')
  },
  {
    name: 'lowercase',
    color: '#9BAADD',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: [],
    previewSupport: getTransformFunctionPreviewSupport('lowercase')
  },
  {
    name: 'uppercase',
    color: '#9BAADD',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: [],
    previewSupport: getTransformFunctionPreviewSupport('uppercase')
  },
  {
    name: 'collapse_space',
    color: '#9BAADD',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: [],
    previewSupport: getTransformFunctionPreviewSupport('collapse_space')
  },
  {
    name: 'keep_digits',
    color: '#9BAADD',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: [],
    previewSupport: getTransformFunctionPreviewSupport('keep_digits')
  },
  {
    name: 'remove_prefix',
    color: '#F2EAC4',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Prefix', type: 'string' }]),
    previewSupport: getTransformFunctionPreviewSupport('remove_prefix')
  },
  {
    name: 'remove_suffix',
    color: '#95DBDA',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Suffix', type: 'string' }]),
    previewSupport: getTransformFunctionPreviewSupport('remove_suffix')
  },
  {
    name: 'default_if_empty',
    color: '#ff758c',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Default Value', type: 'string' }]),
    previewSupport: getTransformFunctionPreviewSupport('default_if_empty')
  },
  {
    name: 'replace_if_not_number',
    color: '#ff758c',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Replacement Value', type: 'string' }]),
    previewSupport: getTransformFunctionPreviewSupport('replace_if_not_number')
  },
  {
    name: 'map_value',
    color: '#d6d445',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([
      { label: 'From 1', type: 'string' },
      { label: 'To 1', type: 'string' }
    ]),
    previewSupport: getTransformFunctionPreviewSupport('map_value')
  },
  {
    name: 'add',
    color: '#4DC0BF',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Number', type: 'float' }]),
    previewSupport: getTransformFunctionPreviewSupport('add')
  },
  {
    name: 'subtract',
    color: '#4DC0BF',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Number', type: 'float' }]),
    previewSupport: getTransformFunctionPreviewSupport('subtract')
  },
  {
    name: 'multiply',
    color: '#4DC0BF',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Number', type: 'float' }]),
    previewSupport: getTransformFunctionPreviewSupport('multiply')
  },
  {
    name: 'divide',
    color: '#4DC0BF',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Number', type: 'float' }]),
    previewSupport: getTransformFunctionPreviewSupport('divide')
  },
  {
    name: 'round',
    color: '#4DC0BF',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Decimal Places', type: 'int', value: '0' }]),
    previewSupport: getTransformFunctionPreviewSupport('round')
  },
  {
    name: 'ceil',
    color: '#4DC0BF',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: [],
    previewSupport: getTransformFunctionPreviewSupport('ceil')
  },
  {
    name: 'floor',
    color: '#4DC0BF',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: [],
    previewSupport: getTransformFunctionPreviewSupport('floor')
  },
  {
    name: 'format_decimal',
    color: '#4DC0BF',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([{ label: 'Decimal Places', type: 'int', value: '0' }]),
    previewSupport: getTransformFunctionPreviewSupport('format_decimal')
  },
  {
    name: 'clamp',
    color: '#4DC0BF',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([
      { label: 'Min', type: 'float' },
      { label: 'Max', type: 'float' }
    ]),
    previewSupport: getTransformFunctionPreviewSupport('clamp')
  },
  {
    name: 'sum_fields',
    color: '#4DC0BF',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: createParams([
      { label: 'Field 1', type: 'string' },
      { label: 'Field 2', type: 'string' }
    ]),
    previewSupport: getTransformFunctionPreviewSupport('sum_fields')
  },
  {
    name: 'convert_redirect_id_to_contact_id',
    color: '#d6d445',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: [],
    previewSupport: getTransformFunctionPreviewSupport('convert_redirect_id_to_contact_id')
  },
  {
    name: 'unix_timestamp_to_datetime',
    color: '#d9771c',
    enableDefault: false,
    default: '',
    defaultUseRef: false,
    params: [],
    previewSupport: getTransformFunctionPreviewSupport('unix_timestamp_to_datetime')
  }
].sort((a, b) => a.name.localeCompare(b.name));

export function getTransformScriptCoverage(functionNames: string[]) {
  const exact = new Set<string>();
  const approx = new Set<string>();

  for (const name of functionNames) {
    if (getTransformFunctionPreviewSupport(name) === 'approx') {
      approx.add(name);
    } else {
      exact.add(name);
    }
  }

  return {
    exact: Array.from(exact).sort(),
    approx: Array.from(approx).sort()
  };
}

export function buildTransformScript(functions: TransformFunctionItem[]) {
  return functions
    .map(item => `${item.name}${formatFunctionParams(item)}`)
    .join(' |\n');
}

export function formatFunctionParams(func: Pick<TransformFunctionItem, 'params' | 'enableDefault' | 'default' | 'defaultUseRef'>) {
  const params = func.params ?? [];

  const formattedParams = params.map(param => {
    if (param.useRef) {
      return formatTransformScriptReference(param.value);
    }

    if (param.type === 'string') {
      return `"${param.value.replaceAll('"', '\\"')}"`;
    }

    if (param.type === 'boolean') {
      return param.value === 'true' ? 'true' : 'false';
    }

    let num = Number(param.value);
    if (Number.isNaN(num)) {
      num = 0;
    }

    if (param.type === 'int') {
      return Number.isInteger(num) ? String(num) : String(Math.floor(num));
    }

    return Number.isInteger(num) ? `${num}.0` : String(param.value);
  });

  let result = `(${formattedParams.join(', ')})`;

  if (func.enableDefault) {
    result += func.defaultUseRef ? `?:${formatTransformScriptReference(func.default)}` : `?:"${func.default}"`;
  }

  return result;
}

export function hydrateTransformFunctions(script: string) {
  const statements = parseTransformScript(script);

  return statements.flatMap((statement, index) => {
    const template = transformScriptFunctionCatalog.find(item => item.name === statement.functionName);

    if (!template) {
      return [];
    }

    const hydrated: TransformFunctionItem = {
      ...structuredClone(template),
      id: `${statement.functionName}-${index}-${crypto.randomUUID()}`
    };

    if (statement.defaultValue) {
      hydrated.enableDefault = true;
      hydrated.defaultUseRef = statement.defaultValue.type === 'reference';
      hydrated.default = statement.defaultValue.value;
    }

    statement.param.forEach((param, paramIndex) => {
      if (!hydrated.params[paramIndex]) {
        hydrated.params.push({
          label: hydrated.name === 'map_value'
            ? `${paramIndex % 2 === 0 ? 'From' : 'To'} ${Math.floor(paramIndex / 2) + 1}`
            : `Field ${paramIndex + 1}`,
          type: 'string',
          value: '',
          useRef: false
        });
      }

      hydrated.params[paramIndex].useRef = param.type === 'reference';
      hydrated.params[paramIndex].value = param.value;
    });

    return hydrated;
  });
}

export function createTransformFunctionItem(name: string) {
  const template = transformScriptFunctionCatalog.find(item => item.name === name);

  if (!template) {
    return null;
  }

  return {
    ...structuredClone(template),
    id: crypto.randomUUID()
  } satisfies TransformFunctionItem;
}

export function buildTransformAttributeReferenceList(keys: string[], staticKeys: string[]): TransformAttributeReferenceItem[] {
  return [
    ...staticKeys.map(item => ({ value: item, label: item, refType: 'static' as const })),
    ...keys.map(item => ({ value: item, label: item, refType: 'attribute' as const }))
  ];
}

export function renderTransformScriptPreview(script: string) {
  const stack = parseTransformScript(script);

  return stack.map(statement => {
    const params = statement.param.map((param) => {
      if (param.type === 'string') {
        return { kind: 'string', text: `"${param.value.replaceAll('"', '\\"')}"` };
      }

      if (param.type === 'boolean') {
        return { kind: 'boolean', text: param.value };
      }

      if (param.type === 'reference') {
        return { kind: 'reference', text: formatTransformScriptReference(param.value) };
      }

      return { kind: 'number', text: param.value };
    });

    let defaultValue: { kind: string; text: string } | null = null;
    const source = statement.defaultValue;

    if (source) {
      if (source.type === 'string') {
        defaultValue = { kind: 'string', text: `"${source.value}"` };
      } else if (source.type === 'boolean') {
        defaultValue = { kind: 'boolean', text: source.value };
    } else if (source.type === 'reference') {
        defaultValue = { kind: 'reference', text: formatTransformScriptReference(source.value) };
      } else {
        defaultValue = { kind: 'number', text: source.value };
      }
    }

    return {
      functionName: statement.functionName,
      params,
      defaultValue
    };
  });
}
